<template>
  <div class="container calendar__wrapper"
       :class="{'calendar__show-weekend': isShowWeekendsWeekView, 'calendar__day-week-view': isDayWeekView}">
    <div class="calendar__title-wrapper">
      <div class="calendar__arrow-left" @click="navigateCalendar('prev')"></div>
      <h1 class="calendar__title">Calendar: {{ currentDate }}</h1>
      <div class="calendar__arrow-right" @click="navigateCalendar('next')"></div>
      <template v-if="isDataFetched">
        <button class="calendar__today" @click.stop.prevent="setToday">TODAY</button>
        <base-check-box class="calendar__show-weekends"
                        :style="{marginRight: isDatePlannerVisible ? '644px' : '209px'}"
                        label="Show weekends"
                        v-model="showWeekends"/>
      </template>
    </div>
    <div class="calendar__loader" v-if="!isDataFetched">
      <base-loader/>
    </div>
    <template v-else>
      <div class="calendar__content-wrapper">
        <div class="calendar__header-button-wrapper">
          <button class="calendar__copy-button"
                  :style="{opacity: enabledCopyFiltersButton ? 1 : 0.5}"
                  @click.stop="copyFilters">
            <base-material-design-icon style="margin-right: 8px; font-size: 14px;" icon="copy"/>
            copy filters
          </button>

          <button
            @click.stop="clearFilters"
            :style="{opacity: enabledClearFiltersButton ? 1 : 0.5}"
            class="calendar__clear-filters">
            <span>clear all</span>
            <span class="calendar__cross">&times;</span>
          </button>

          <div class="calendar__export-csv" @click="exportCsv" v-if="isExportButtonVisible">
            <base-material-design-icon icon="download"/>
          </div>
        </div>
        <calendar-header-nav-panel
          ref="chips-block"
          class="calendar__chips-block"
          :filtered-statuses="filteredUrlStatuses"
          :filtered-types="filteredTypes"
          :filtered-page-types="filteredPageTypes"
          :available-page-types="availablePageTypes"
          :filters-from-url="filtersFromUrl"
          @clear="clearResetAction"
          v-model="filters"
        />
        <FullCalendar
          class='calendar'
          ref="fullCalendar"
          defaultView="dayGridMonth"
          :contentHeight="contentHeight"
          editable="false"
          :eventLimit="eventsLimit"
          eventLimitText=""
          :droppable="false"
          :weekends="showWeekends"
          :header="{
            center: '',
            right: 'dayGridDay, dayGridWeek, dayGridMonth',
            left: ''
            // center: 'title',
            // right: 'prev, today, next',
            // left: 'listWeek, dayGridDay, dayGridWeek, dayGridMonth'
            }"
          :plugins="calendarPlugins"
          :events="filteredEvents"
          @eventClick="onEventClick"
          navLinks="true"
          :datesRender="datesRender"
          :navLinkDayClick="handleDateClick"
          :dayRender="dayRender"
          :event-render="eventRender"
          :viewSkeletonRender="onRender"
          @eventDrop="eventDrop"
          :eventLimitClick="moreLinkClick"
          @eventReceive="externalEventReceived"
          :displayEventTime="false"
          :timeZone="'UTC'"
          :columnHeaderFormat="{weekday: 'narrow'}"

        />

        <div class="calendar__update-loader" v-if="isDataUpdating">
          <base-loader class="calendar__update-loader-ring"/>
        </div>
        <div class="calendar__date-planner-wrapper" v-if="isDatePlannerVisible">
          <calendar-date-planner class="calendar__date-planner"
                                 ref="date-planner"
                                 @navigation="onNavigation($event)"></calendar-date-planner>
        </div>
      </div>
      <calendar-right-nav-panel class="calendar__right-panel"
                                @navigation="onNavigation($event)"
                                :highlight="getHighlightRightPanelIcon"
                                v-if="!isCurrentUserClientCollaborator"
      />

    </template>
    <modal name="event"
           v-bind="modalProps">
      <create-event-modal
        @apply="onSubmitEventModal($event)"
        :event="updatingEvent"
        @delete="onDeleteEvent"
        @cancel="closeEventModal"/>
    </modal>
    <modal name="create-url"
           v-bind="modalProps">
      <CreateUrlPopup
        ref="create-url"
        @apply="createNewUrl($event)"
        @cancel="closeCreateUrlModal"/>
    </modal>
    <modal name="edit-url"
           v-bind="modalProps">
      <EditUrlPopup
        hideHeader
        :url="updatingEvent"
        @apply="editUrl($event)"
        @cancel="closeEditUrlModal"/>
    </modal>
    <modal name="link"
           v-bind="modalProps">
      <LinkPopup
        :link="updatingEvent"
        @cancel="closeLinkModal"/>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import listWeek from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import moment from 'moment'
import _ from 'lodash'
import baseModalProps from '@/common/BaseModalProps'
import CalendarHeaderNavPanel from './CalendarHeaderNavPanel'
import CalendarRightNavPanel from './CalendarRightNavPanel'
import { ALLOWED_URL_STATUS_NAMES_CALENDAR } from '@/common/AllowedUrlStatuses'
import CalendarDatePlanner from './CalendarDatePlanner'
import CreateEventModal from './CreateEventPopup'
import CreateUrlPopup from '@/features/url-planning/CreateUrlPopup'
import EditUrlPopup from '@/features/calendar/EditUrlPopup'
import LinkPopup from './LinkPopup'
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import { convertDate, convertDateToUTC, convertTimezone } from './DateHelper'
import csvExport from '@/services/CSVExport'
import filters from '@/common/Filters'
import Tooltip from 'tooltip.js'
import ToasterService from '@/services/toasterService'

const CONTENT_HEIGHT_FULL = 800
const EVENTS_LIMIT_MONTH = 3
const EVENTS_LIMIT_DAY_WEEK = 25
// const MIN_CALENDAR_WIDTH_FOR_HEADER_RESIZING = 1400
export const FILTER_ACTION = {
  // this action means that event set was reset / loaded
  RESET: 'reset',
  // set when event list or separate event were updated (changed, deleted, external event dropped so on)
  UPDATE: 'update',
  // pass means that datesRender callback was invoked already for this event set
  PASS: 'pass'
}
/**
 * Calendar component
 */
export default {
  name: 'Calendar',
  // this props to open specific event for editing using url query data
  props: ['modal', 'url-filters'],
  components: {
    LinkPopup,
    CreateEventModal,
    CalendarDatePlanner: () => import('./CalendarDatePlanner'),
    CalendarRightNavPanel,
    CalendarHeaderNavPanel,
    CreateUrlPopup,
    EditUrlPopup,
    FullCalendar
  },
  // mounted () {
  //   window.addEventListener('resize', _.debounce(() => {
  //     this.onWindowResize()
  //   }, 100, { 'trailing': true }))
  // },
  updated () {
    this.addEllipsis()
  },
  data: function () {
    return {
      calendarPlugins: [
        listWeek,
        dayGridPlugin,
        interactionPlugin,
        momentTimezonePlugin
      ],
      // array of all calendar events
      calendarEvents: [],
      startDate: {},
      endDate: {},
      contentHeight: CONTENT_HEIGHT_FULL,
      eventsLimit: EVENTS_LIMIT_MONTH,
      // url statuses that exist in Publishing events in current calendar view
      filteredUrlStatuses: {},
      // page types that exist in selected URL statuses in current calendar view
      filteredPageTypes: {},
      // event types that are present in current calendar view
      filteredTypes: {},
      // list of all events filtered according to this.filters settings
      filteredEvents: [],
      // list of available page types according existing URL statuses
      availablePageTypes: [],
      // current visible date range
      currentStart: null,
      currentEnd: null,
      isDatePlannerVisible: false,
      showWeekends: false,
      debouncedFunction: null,
      calendarAPI: null,
      currentDate: '',
      calendarType: '',
      // filters selected from url
      filtersFromUrl: {},
      /**
       * @alias calendarFilters
       * filters that set from CalendarHeaderNavPanel.
       * Be careful - this.filters is bound to the CalendarHeaderNavPanel as modelValue so change this in the calendar component
       * may have unexpected consequences in child components
       * */
      filters: {
        selectedTypes: [],
        selectedStatuses: [],
        selectedPageTypes: []
      },
      // this flag manages position of top navigation by shrinking screen
      isDataFetched: false,
      isDataUpdating: false,
      // event that is in update state using corresponding modal
      updatingEvent: null,
      filterAction: FILTER_ACTION.RESET,
      // month date start received from url
      dateStart: null,
      enabledClearFiltersButton: false,
      updateCopyFilterButton: false,
      enabledCopyFiltersButton: true,
      calendarTypeView: null
    }
  },
  watch: {
    selectedClient: {
      async handler (selectedClient) {
        if (!selectedClient) {
          return
        }
        // logic for opening specific event for editing using url query data
        if (this.modal) {
          this.modal = JSON.parse(this.modal)
          const clientId = this.modal.client_id

          if (clientId !== selectedClient._id) {
            // if currentClient !== modal client we need to set new client and this handler will be invoked again
            if (!this.setNewClient(clientId, true)) {
              console.warn('calendar client with id ', clientId, 'does not connected to current user')
              this.modal = null
            }
            return
          }
        }
        this.isDataFetched = false
        this.isDatePlannerVisible = false
        this.filterAction = FILTER_ACTION.RESET
        this.resetFilters()
        this.resetData()
        await this.loadData()
        if (this.modal) {
          this.openModal()
        }
      },
      immediate: true

    },
    currentStart () {
      this.enabledCopyFiltersButton = true
    },
    calendar: {
      handler: 'prepareData',
      deep: true
    },
    calendarType: 'prepareData',
    showWeekends () {
      const calendarApi = this.$refs.fullCalendar.getApi()
      this.setCurrentDate(calendarApi.state.dateProfile.currentRange)
      this.$nextTick(() => {
        this.prepareData()
      })
    },
    /**
     * filters managed by {@link CalendarHeaderNavPanel}
     * @alias navFilters
     * */
    filters: {
      handler () {
        if (!this.isDataFetched) {
          return
        }
        this.updateFilteredEvents()
        this.enabledClearFiltersButton = true
        this.enabledCopyFiltersButton = true
        // update only when filter action in filter types not equal RESET
        // it has created because this function handling changes in CalendarHeaderNavPanel
        // where URLStatuses filter emit change event earlier than works function that handle RESET action in PageTypes filter
        if (this.filters.selectedStatuses.length >= 1 && this.filteredPageTypes.filterAction !== FILTER_ACTION.RESET) {
          // update filtered page types according page statuses
          this.updateFilteredPageTypes()
        }
      },
      deep: true
    }
    //
    // isDataFetched (value) {
    //   if (!value) {
    //     return
    //   }
    //   setTimeout(() => {
    //     // hack to calculate calendar after filling by received date because this process takes some time
    //     this.onWindowResize()
    //   }, 1000)
    // }
  },
  computed: {
    ...mapState({
      selectedClient: 'selectedClient',
      urlStatuses: 'urlStatuses'
    }),
    ...mapState('calendar', ['calendar', 'startDate', 'endDate', 'isDataUpdating']),
    ...mapState('urlPlanning', ['urls']),
    isSelectedLink () {
      const selectedTypes = this.filters.selectedTypes
      return selectedTypes.includes('link')
    },
    isSelectedUrl () {
      const selectedTypes = this.filters.selectedTypes
      return selectedTypes.includes('url')
    },
    isSelectedEvent () {
      const selectedTypes = this.filters.selectedTypes
      return selectedTypes.includes('event')
    },
    getHighlightRightPanelIcon () {
      if (this.isDatePlannerVisible) {
        return 'calendar'
      }
    },
    modalProps: function () {
      const modalProps = {
        width: 666
      }
      return { ...baseModalProps, ...modalProps }
    },
    isShowWeekendsWeekView () {
      return this.showWeekends && this.calendarType === 'dayGridWeek'
    },
    isDayWeekView () {
      return ['dayGridDay', 'dayGridWeek'].includes(this.calendarType)
    },
    isExportButtonVisible () {
      return this.isDataFetched && this.calendarEvents && this.calendarEvents.length
    }
  },
  methods: {
    ...mapActions('calendar', [
      'getCalendar', 'changeEvent', 'createEvent', 'deleteEvent'
    ]),
    ...mapActions('urlPlanning', [
      'changeStatus', 'createUrl', 'getUrlPlannerData'
    ]),
    resetData () {
      this.filteredEvents = []
      this.calendarEvents = []
    },
    resetFilters () {
      this.currentStart = null
      this.currentEnd = null
      this.filteredUrlStatuses = {}
      this.filteredTypes = {}
      this.filteredPageTypes = {}
      this.$set(this.filters, 'selectedTypes', [])
      this.$set(this.filters, 'selectedStatuses', [])
      this.$set(this.filters, 'selectedPageTypes', [])
    },
    loadData: async function () {
      if (this.selectedClient) {
        // get filters from URL
        this.setupFilterParams()
        this.endDate = new Date().setDate(365)
        this.startDate = new Date().setDate(-365)
        await Promise.all([
          this.getCalendar({
            clientId: this.clientId,
            filters: {
              // for now we a receiving data for period of 2 years: 1 year before today date and 1 year after
              date_from: moment(this.startDate).format('YYYY-MM-DD'),
              date_to: moment(this.endDate).format('YYYY-MM-DD')
            }
          })])
        this.isDataFetched = true
      }
    },
    /**
     * invokes when the event was moved into another date
     */
    async eventDrop ({ event }) {
      const serverEvent = event.extendedProps.event
      this.isDataUpdating = true
      const data = {
        clientId: this.clientId,
        eventId: event.id,
        type: serverEvent.type
      }
      data.start_date = convertDateToUTC(event.start, serverEvent.start_date, 'YYYY-MM-DD HH:mm:ss')
      if (data.type === 'event') {
        let endDate = event.end
        if (endDate) {
          data.end_date = convertDateToUTC(endDate, serverEvent.end_date, 'YYYY-MM-DD HH:mm:ss')
        } else {
          data.end_date = data.start_date
        }
      }
      await this.changeEvent(data)
      await this.loadData()
      this.isDataUpdating = false
    },
    /**
     * invokes when the external event was dropped into some date
     * source of external events - {@link CalendarDatePlanner}
     */
    async externalEventReceived (data) {
      const { draggedEl, event } = data
      const date = event.start
      event.remove()
      if (this.isDataUpdating || this.calendarEvents.some(item => item.id === event._id)) {
        return
      }
      let url = _.get(draggedEl, 'dataset.event', null)
      if (url) {
        url = JSON.parse(url)
        try {
          this.isDataUpdating = true
          const data = {
            clientId: this.clientId,
            eventId: url._id,
            type: 'url',
            start_date: moment.utc(date).format('YYYY-MM-DD'),
            end_date: moment.utc(date).format('YYYY-MM-DD')
          }
          await this.changeEvent(data)
          await this.loadData()
          // remove dropped event from external events list in CalendarDatePlanner
          this.$refs['date-planner'] && this.$refs['date-planner'].removeUrl(url._id)
        } catch (e) {
        } finally {
          this.filterAction = FILTER_ACTION.UPDATE
          this.isDataUpdating = false
        }
      }
    },
    getClassName (event) {
      switch (event.type) {
        case 'link':
          return ['calendar__link']
        case 'event':
          return ['calendar__event']
        case 'url':
          switch (event.status.name) {
            case 'Published':
              return ['calendar__url', 'calendar__url-published']
            case 'Proposed':
              return ['calendar__url', 'calendar__url-proposed']
            case 'Planned':
              return ['calendar__url', 'calendar__url-planned']
            default:
              return ['calendar__url', 'calendar__url-planned']
          }
        default:
          return ['calendar__url', 'calendar__url-planned']
      }
    },
    /**
     * function that converts backend data into calendar event
     * */
    convertDataToEvent (event) {
      const classNames = this.getClassName(event)
      // const color = event.type === 'link' ? '#284aff' : event.type === 'event' ? '#d88b23' : '#24756d'
      // if (event.type === 'event' && !event.link) {
      //   classNames.push('calendar__cell_disabled')
      // }
      if (!event.start_date) {
        console.warn(`${event} dos not have a start date!`)
        return
      }
      // Because of customer requirements we need to supply the same event date for all timezones
      // To rich that we recalculate all dates to UTC time
      let start = convertTimezone(event.start_date, event.type)
      let end = convertTimezone(event.end_date, event.type, true)
      if (event.type === 'event') {
        if (!end) {
          end = start
        }
      }
      const editable = !this.isCurrentUserClientCollaborator && (event.type !== 'link')

      let title = event.name

      if (this.calendarType !== 'dayGridMonth' && event.type !== 'link') {
        if (event.type === 'url') {
          const truncatedUrl = this.getSerpTruncatedUrl(event.url, this.showWeekends ? 48 : 68)
          title = `
            <div class="serp-simulator">
              <a href="${event.url}" target="_blank" class="serp-simulator__url">
                ${truncatedUrl || ''}
              </a>
              <a href="${event.url}" target="_blank" class="serp-simulator__title">${_.get(event, 'tag_title', '') || ''}</a>
              <div class="serp-simulator__description">${_.get(event, 'tag_meta_description', '') || ''}</div>
            </div>
          `
        } else if (event.type === 'event') {
          title = `
            <div class="event-week-view">
              <div class="event-week-view__title">${event.name}</div>
              <div class="event-week-view__desc">${event.description || ''}</div>
            </div>
          `
        }
      }

      const newEvent = {
        event,
        title,
        start,
        end,
        id: event._id,
        status: event.status,
        editable,
        classNames,
        type: event.type
      }
      return newEvent
    },
    /**
     * method filters events according to this.filters {@link navFilters} setting
     * */
    updateFilteredEvents () {
      const filtered = this.calendarEvents.filter(item => {
        if (item.type === 'url') {
          if (item.event.page_type.name === 'Editorial' || !item.event.page_type.name) {
            return this.isSelectedUrl && this.filters.selectedStatuses.includes(item.status._id) && this.filters.selectedPageTypes.includes('editorial')
          } else {
            return this.isSelectedUrl && this.filters.selectedStatuses.includes(item.status._id) && this.filters.selectedPageTypes.includes(item.event.page_type._id)
          }
        } else if (item.type === 'link') {
          return this.isSelectedLink
        } else if (item.type === 'event') {
          return this.isSelectedEvent
        }
      })
      this.filteredEvents = _.cloneDeep(filtered)
    },
    updateFilteredPageTypes () {
      let events = _.cloneDeep(this.calendarEvents)
      // get events for this mounth
      events = events.filter(event => {
        return moment(event.start).isBetween(this.currentStart, this.currentEnd) || moment(event.start).isSame(this.currentStart)
      })
      const filteredPageTypes = []
      events.forEach(item => {
        if (item.type === 'url' && this.isSelectedUrl && this.filters.selectedStatuses.includes(item.status._id)) {
          // check is pagetype exist
          const existPageType = filteredPageTypes.some(pageType => pageType === item.event.page_type._id)
          // add page type if he haven't added to list and page type not Editorial
          if (item.event.page_type._id && !existPageType && item.event.page_type.name !== 'Editorial') {
            filteredPageTypes.push(item.event.page_type._id)
          }
        }
      })
      // add editoral page type as default
      filteredPageTypes.unshift('editorial')
      this.filteredPageTypes = { statuses: filteredPageTypes, filterAction: FILTER_ACTION.UPDATE }
    },
    handleDateClick (date, jsEvent) {
      const calendarApi = this.$refs.fullCalendar.getApi()
      calendarApi.changeView('dayGridDay')
      calendarApi.gotoDate(date)
    },
    moreLinkClick () {
      this.$nextTick(() => {
        this.addEllipsis()
      })
      return 'popover'
    },
    /**
     * callback to change UI today date cell
     * */
    dayRender ({ date, el, view }) {
      if (this.calendarType !== view.type || !view.type) {
        this.calendarType = view.type
        this.setCurrentDate(this.$refs.fullCalendar.getApi().state.dateProfile.currentRange)
      }
      if (moment().isSame(date, 'day')) {
        el.style = 'background-color: #f8f8f8;'
      } else {
        el.style = 'background-color: white;'
      }
    },
    /**
     * this callback would be helpfull for attaching tooltip to events
     * */
    eventRender ({ el, event }) {
      el.innerHTML = el.innerHTML.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/<br>/g, '')
      // adding the same classes as in a target
      let className = null
      switch (true) {
        case event.extendedProps.type === 'link':
          className = 'tooltip-inner-links'
          break
        case event.extendedProps.type === 'event':
          className = 'tooltip-inner-event'
          break
        case event.extendedProps.type === 'url':
          switch (true) {
            case event.extendedProps.status.name === 'Planned':
              className = 'tooltip-inner-url-planned'
              break
            case event.extendedProps.status.name === 'Proposed':
              className = 'tooltip-inner-url-proposed'
              break
            case event.extendedProps.status.name === 'Published':
              className = 'tooltip-inner-url-published'
              break
          }
      }
      const data = event.extendedProps.event || event.extendedProps
      // eslint-disable-next-line no-unused-vars
      let title = ''
      try {
        if (event.extendedProps.type === 'link' || event.extendedProps.type === 'event') {
          title = data.name
        } else if (event.extendedProps.type === 'url') {
          title = `${data.page_type.name ? data.page_type.name : 'Editorial'} - ${data.status.name}`
        }
      } catch (e) {
        title = 'Editorial'
      }
      if (this.calendarType === 'dayGridMonth') {
        let template
        if (data.type === 'url') {
          const truncatedUrl = this.getSerpTruncatedUrl(data.url || data.name)
          template = `
            <div class="tooltip top" role="tooltip">
              <div class="serp-simulator calendar__tooltip">
                <div class="tooltip-inner"></div>
                <a href="${data.url}" target="_blank" class="serp-simulator__url">
                  ${truncatedUrl || ''}
                </a>
                <a href="${data.url}" target="_blank" class="serp-simulator__title">${_.get(data, 'tag_title', '') || ''}</a>
                <div class="serp-simulator__description">${_.get(data, 'tag_meta_description', '') || ''}</div>
              </div>
            </div>
          `
        } else {
          template = `
            <div class="tooltip top" role="tooltip">
              <div class="tooltip-inner ${className}"></div>
              <div class="tooltip-arrow-wrapper">
                  <div class="tooltip-arrow ${className}"></div>
              </div>
            </div>
          `
        }
        // eslint-disable-next-line no-unused-vars
        const tooltip = new Tooltip(el, {
          title,
          trigger: 'hover',
          template,
          container: '.calendar__wrapper'
        })
      }
    },
    /**
     * callback to open mpdal by click on event
     * */
    onEventClick ({ event, jsEvent }) {
      jsEvent.preventDefault()
      // remove all created tooltips
      const tooltips = document.querySelectorAll('.tooltip')
      tooltips.forEach(tooltip => tooltip && tooltip.remove())
      const calendarEvent = event.extendedProps.event
      if (calendarEvent.type === 'link') {
        this.openLinkModal(calendarEvent)
      } else if (calendarEvent.type === 'event') {
        this.openEventModal(calendarEvent)
      } else if (calendarEvent.type === 'url') {
        this.openEditUrlModal(calendarEvent)
      }
    },
    getSerpTruncatedUrl (url, maxUrlLength = 60) {
      const defaultMaxUrlLength = maxUrlLength
      const protocolsArr = ['http://', 'https://']
      protocolsArr.some(i => {
        if (url.startsWith(i)) {
          url = url.replace(i, '')
          return true
        }
      })
      if (url.endsWith('/')) {
        url = url.slice(0, -1)
      }
      const slashesCount = (url.match(/\//g) || []).length
      let truncatedUrl = ''
      const urlArray = url.split('/')
      maxUrlLength -= urlArray[0].length + 12
      truncatedUrl = `<span style="color: #202124; margin-right: 3px;">${urlArray[0]}</span>`
      urlArray.shift()
      const customizedUrl = urlArray.join(' > ')
      if (url.length <= (defaultMaxUrlLength - 2) - slashesCount * 3) {
        truncatedUrl += '> ' + customizedUrl
      } else {
        let endOfTruncatedUrl = customizedUrl.slice(maxUrlLength * -1)
        const arr = [' > ', '> ', ' ']
        arr.some(i => {
          if (endOfTruncatedUrl.startsWith(i)) {
            endOfTruncatedUrl = endOfTruncatedUrl.replace(i, '')
            return true
          }
        })
        truncatedUrl += ' > ... > ' + endOfTruncatedUrl
      }
      return truncatedUrl
    },
    getTruncatedUrl (url) {
      const truncateUrl = url.replace(/(^\w+:|^)\/\//, '')
      if (truncateUrl.length <= 60) {
        return truncateUrl
      }
      const splitedUrl = truncateUrl.split('/')
      const lastSymbolsCount = 57 - splitedUrl[0].length
      return `${splitedUrl[0]}...${truncateUrl.slice(truncateUrl.length - lastSymbolsCount)}`
    },
    setToday () {
      const calendarApi = this.$refs.fullCalendar.getApi()
      calendarApi.today()
      this.setCurrentDate(calendarApi.state.dateProfile.currentRange)
    },
    navigateCalendar (direction) {
      const calendarApi = this.$refs.fullCalendar.getApi()
      calendarApi[direction]()
      this.setCurrentDate(calendarApi.state.dateProfile.currentRange)
    },
    setCurrentDate (date) {
      // let startDate = moment(date.start).format('MMM D')
      let startDate = moment(date.start)
      let endDate = moment(date.end)
      if (this.calendarType === 'dayGridWeek') {
        if (this.showWeekends) {
          startDate = startDate.format('MMM D')
        } else {
          startDate = startDate.add(1, 'd').format('MMM D')
        }
        if (this.showWeekends) {
          endDate = endDate.subtract(1, 'd').format('MMM D, YYYY')
        } else {
          endDate = endDate.subtract(2, 'd').format('MMM D, YYYY')
        }
        this.currentDate = `${startDate} - ${endDate}`
      } else if (this.calendarType === 'dayGridMonth') {
        const day = startDate.isoWeekday()
        if (this.showWeekends || ![6, 7].includes(day)) {
          startDate = startDate.format('MMM D')
        } else {
          startDate = startDate.add(1, 'weeks').isoWeekday(1).format('MMM D')
        }
        endDate = endDate.subtract(1, 'd').format('MMM D, YYYY')
        this.currentDate = `${startDate} - ${endDate}`
      } else {
        this.currentDate = startDate.format('MMM D, YYYY')
      }
    },
    /**
     * This callback will get triggered when the initial view renders or when the user changes the view,
     * but before the datesRender callback fires, which is a callback for when all date/time cells have been rendered.
     * @see https://fullcalendar.io/docs/viewSkeletonRender
     * */
    onRender ({ view }) {
      const scroller = document.querySelector('.fc-scroller')
      if (!scroller) {
        return
      }
      if (view.viewSpec.type === 'dayGridMonth') {
        this.eventsLimit = EVENTS_LIMIT_MONTH
        scroller.style['overflow-y'] = 'hidden !important'
      } else {
        this.eventsLimit = EVENTS_LIMIT_DAY_WEEK
        scroller.style['overflow-y'] = 'auto'
      }
    },
    /**
     * Triggered when a new set of dates has been rendered. @see https://fullcalendar.io/docs/datesRender
     * we need it to accomplish current view (month, day so on) specific logic, preferably to change {@link CalendarHeaderNavPanel} settings
     * */
    datesRender ({ view }) {
      // check to avoid infinite rerendering calendar
      if (moment(view.currentStart).isSame(this.currentStart) && moment(view.currentEnd).isSame(this.currentEnd)) {
        if (this.filterAction === FILTER_ACTION.PASS) {
          return
        }
      } else if (this.filterAction === FILTER_ACTION.PASS) {
        this.filterAction = FILTER_ACTION.UPDATE
      }
      this.calendarTypeView = view.type
      this.currentStart = view.currentStart
      this.currentEnd = view.currentEnd
      let events = _.cloneDeep(this.calendarEvents)
      // filter only those events that placed in current calendar view
      events = events.filter(event => {
        return moment(event.start).isBetween(this.currentStart, this.currentEnd) || moment(event.start).isSame(this.currentStart)
      })
      // get statuses and types shown events
      const statuses = []
      const types = []
      const pageTypes = []
      events.forEach(item => {
        if (!types.includes(item.type)) {
          types.push(item.type)
        }
        if (item.type !== 'url') {
          return
        }
        const status = item.status
        if (status._id && !statuses.includes(status._id) && ALLOWED_URL_STATUS_NAMES_CALENDAR.includes(status.name.toLowerCase())) {
          statuses.push(status._id)
        }
        // check is page type already added to list
        const existPageType = pageTypes.some(pageType => pageType.value === item.event.page_type._id)
        // add page type if he haven't added to list and page type not Editorial
        if (item.event.page_type.name && !existPageType && item.event.page_type.name !== 'Editorial') {
          pageTypes.push({ label: item.event.page_type.name, value: item.event.page_type._id })
        }
      })

      // set available page types
      this.availablePageTypes = _.orderBy(pageTypes, ['label'], ['asc'])
      // add editoral page type as default
      pageTypes.unshift({ value: 'editorial' })

      if (this.filterAction === FILTER_ACTION.RESET) {
        // reset statuses and types
        this.filteredUrlStatuses = { types: statuses, filterAction: this.filterAction }
        this.filteredTypes = { types, filterAction: this.filterAction }
      } else if (this.filterAction === FILTER_ACTION.UPDATE) {
        // define what was changed and reset filteredTypes and/or filteredUrlStatuses accordingly
        if (this.filteredUrlStatuses.length !== statuses.length) {
          this.filteredUrlStatuses = { types: statuses, filterAction: this.filterAction }
        }
        if (this.filteredTypes.length !== types.length) {
          this.filteredTypes = { types, filterAction: this.filterAction }
        }
      }
      this.filteredPageTypes = { statuses: pageTypes.map(item => item.value), filterAction: this.filterAction }
      // set to PASS to avoid repeated processing
      this.filterAction = FILTER_ACTION.PASS
    },
    /**
     * callback for calendar events received form store
     */
    prepareData: function () {
      const events = [...this.calendar.events, ...this.calendar.links, ...this.calendar.urls]
      if (_.isEmpty(events)) {
        return
      }
      this.resetData()
      this.calendarEvents = events.map(this.convertDataToEvent)
      this.updateFilteredEvents()
    },
    // /**
    //  * resize callback to change top panel position
    //  */
    // onWindowResize() {
    //   const width = _.get(this.$refs, 'fullCalendar.$el.clientWidth', null)
    //   this.isShrinked = width < MIN_CALENDAR_WIDTH_FOR_HEADER_RESIZING
    // },

    onNavigation (event) {
      if (event === 'calendar') {
        this.isDatePlannerVisible = !this.isDatePlannerVisible
        this.$nextTick(this.onWindowResize)
      }
      if (event === 'event') {
        this.isDatePlannerVisible = false
        this.openEventModal()
      }
      if (event === 'url') {
        this.isDatePlannerVisible = false
        this.openCreateUrlModal()
      }
    },
    openModal () {
      const eventId = this.modal.event_id
      const calendarEvent = this.calendarEvents.find(item => item.id === eventId)
      if (calendarEvent) {
        if (calendarEvent.type === 'event') {
          this.openEventModal(calendarEvent.event)
        } else if (calendarEvent.type === 'url') {
          this.openEditUrlModal(calendarEvent.event)
        }
      }
      this.modal = null
    },
    openLinkModal (event) {
      this.updatingEvent = { ...event }
      this.$modal.show('link')
    },
    openEventModal (event) {
      this.updatingEvent = event
      this.$modal.show('event')
    },
    openCreateUrlModal () {
      this.$modal.show('create-url')
    },
    openEditUrlModal (event) {
      this.updatingEvent = { ...event }
      this.$modal.show('edit-url')
    },
    async onSubmitEventModal (event) {
      this.closeEventModal()
      const data = {
        start_date: convertDate(event.start_date),
        end_date: convertDate(event.end_date),
        page_type: event.pageType,
        status: event.status,
        description: event.description,
        name: event.name,
        eventId: event._id,
        clientId: this.clientId,
        type: 'event'
      }
      this.isDataUpdating = true
      try {
        if (event._id) {
          await this.changeEvent({
            ...data,
            eventId: event._id
          })
        } else {
          await this.createEvent({
            ...data
          })
        }
        await this.loadData()
      } catch (e) {
      } finally {
        this.filterAction = FILTER_ACTION.UPDATE
        this.isDataUpdating = false
      }
    },
    async onDeleteEvent (event) {
      this.closeEventModal()
      this.isDataUpdating = true

      try {
        await this.deleteEvent({
          clientId: this.clientId,
          eventId: event._id
        })
        await this.loadData()
      } catch (e) {
      } finally {
        this.filterAction = FILTER_ACTION.UPDATE
        this.isDataUpdating = false
      }
    },
    closeEventModal () {
      this.updatingEvent = null
      this.$modal.hide('event')
    },
    closeCreateUrlModal () {
      this.updatingEvent = null
      this.$modal.hide('create-url')
    },
    closeEditUrlModal () {
      this.updatingEvent = null
      this.$modal.hide('edit-url')
    },
    closeLinkModal () {
      this.updatingEvent = null
      this.$modal.hide('link')
    },
    async editUrl ({ date, status, pageType }) {
      this.$modal.hide('edit-url')
      this.isDataUpdating = true
      date = convertDate(date)
      try {
        await this.changeStatus({
          clientId: this.clientId,
          urlId: this.updatingEvent._id,
          pageType: pageType,
          statusId: status,
          date
        })
        await this.loadData()
      } catch (error) {
      } finally {
        this.filterAction = FILTER_ACTION.UPDATE
        this.isDataUpdating = false
      }
    },
    createNewUrl: async function (urlData) {
      this.isDataUpdating = true
      urlData.date = convertDate(urlData.date)
      let createdUrl = null
      try {
        createdUrl = await this.createUrl({ clientId: this.clientId, ...urlData })
        await this.loadData()
        await Promise.all([
          this.loadData(),
          await this.getUrlPlannerData({
            clientId: this.clientId,
            filters: { url: urlData.url }
          })
        ])
        const editorData = this.urls.find(urlData => urlData._id === createdUrl.data.url_id)
        if (editorData) {
          const formattedEditorData = {}
          formattedEditorData.status = editorData.url_status
          formattedEditorData.tag_title = null
          formattedEditorData.content_brief = editorData.brief_content
          formattedEditorData.brief_status = editorData.brief_status
          formattedEditorData.keywords = editorData.keywords
          formattedEditorData.msv = editorData.msv
          formattedEditorData.section = editorData.section
          formattedEditorData.tag_meta_description = null
          formattedEditorData.page_type = editorData.page_types
          formattedEditorData._id = editorData._id
          formattedEditorData.type = 'url'
          formattedEditorData.start_date = editorData.target_date
          formattedEditorData.tag_h1 = null
          formattedEditorData.name = editorData.brief_content.name
          formattedEditorData.url = editorData.url
          this.openEditUrlModal(formattedEditorData)
        }
        this.$modal.hide('create-url')
      } catch (e) {
        this.$refs['create-url'].showError(e[0])
      } finally {
        this.filterAction = FILTER_ACTION.UPDATE
        this.isDataUpdating = false
      }
    },
    exportCsv () {
      if (this.isDataUpdating) {
        return
      }
      const getHyperLink = (item) => {
        let link = `${window.location.origin}${window.location.pathname}#${this.$route.path}?modal=`
        let params = { client_id: this.clientId, event_id: item._id }
        const uri = encodeURI(`${link}${JSON.stringify(params)}`).replace(',', '%2C')
        return `"${uri}"`
      }
      function handler (columns, type) {
        return item => {
          const row = []
          columns.forEach(name => {
            if (['start_date', 'end_date'].includes(name)) {
              row.push(`"${filters.formattedDate(item[name])}"`)
            } else if (name === 'link') {
              row.push(getHyperLink(item))
            } else if (name === 'type') {
              row.push(`"${type}"`)
            } else {
              const newVar = _.get(item, name, null) || 'n/d'
              row.push(`"${newVar}"`)
            }
          })
          data.push(row)
        }
      }
      const header = ['"Slot Type"', '"Name"', '"URL"', '"Status"', '"Start Date"', '"Section"', '"Page Type"', '"Content Brief Name"', '"Content Brief Status"', '"Link"']
      const data = []
      const columnNames = ['type', 'name', 'url', 'status.name', 'start_date', 'section.name', 'page_type.name', 'content_brief.name', 'brief_status.name', 'link']
      data.push(header)
      this.calendar.events.forEach(handler(columnNames, 'Event'))
      this.calendar.urls.forEach(handler(columnNames, 'Publishing'))
      csvExport(`${this.selectedClient.name.replace(/ /g, '_')}_Calendar_${moment().format('YYYY-MM-DD')}`, data)
    },
    /**
     * copy selected filters to clipboard
     */
    copyFilters () {
      if (!this.enabledCopyFiltersButton) {
        return
      }
      let path = `${window.location.origin}${window.location.pathname}#${this.$route.path}?filters=`
      const filters = {
        selectedTypes: [],
        selectedStatuses: [],
        selectedPageTypes: []
      }
      this.filters.selectedTypes.forEach(item => {
        if (this.filteredTypes.types.includes(item)) filters.selectedTypes.push(item)
      })
      if (!filters.selectedTypes.length) {
        filters.selectedTypes.push('none')
        filters.selectedStatuses.push('none')
        filters.selectedPageTypes.push('none')
      } else {
        const selectedStatusesLength = this.filters.selectedStatuses.includes('all') ? 2 : 1
        // check is the URL selected and exist url records
        if (filters.selectedTypes.includes('url') && this.filters.selectedStatuses.length >= selectedStatusesLength) {
          this.filters.selectedStatuses.forEach(item => {
            if (this.filteredUrlStatuses.types.includes(item)) filters.selectedStatuses.push(item)
          })
          // check is statuses selected
          if (this.filters.selectedStatuses.some(item => this.filteredUrlStatuses.types.includes(item)) && this.filters.selectedPageTypes.length) {
            this.filters.selectedPageTypes.forEach(item => {
              if (this.filteredPageTypes.statuses.includes(item)) filters.selectedPageTypes.push(item)
            })
          } else {
            filters.selectedPageTypes.push('none')
          }
        } else {
          filters.selectedStatuses.push('none')
          filters.selectedPageTypes.push('none')
        }
      }
      const copiedFilters = { client_id: this.clientId, dateStart: this.currentStart, calendarTypeView: this.calendarTypeView, filters }
      path += JSON.stringify(copiedFilters)
      path = encodeURI(path)
      this.$copyText(path)
        .then(
          e => {
            ToasterService.showToast(ToasterService.INFO_TYPE, 'Filters copied to clipboard')
          },
          // eslint-disable-next-line handle-callback-err
          err => {
            ToasterService.showToast(ToasterService.ERROR_TYPE, 'Can not copy filters')
          }
        )
    },
    setupFilterParams () {
      if (!this.urlFilters) {
        return
      }
      const filters = JSON.parse(this.urlFilters)
      if (filters && filters instanceof Object) {
        try {
          // data validator
          if (!filters.filters ||
            !filters.calendarTypeView ||
            !filters.filters.selectedTypes ||
            !filters.filters.selectedStatuses ||
            !filters.filters.selectedPageTypes ||
            !filters.dateStart ||
            !filters.client_id ||
            !(filters.filters instanceof Object) ||
            !(filters.filters.selectedTypes instanceof Array) ||
            !(filters.filters.selectedStatuses instanceof Array) ||
            !(filters.filters.selectedPageTypes instanceof Array) ||
            !(typeof filters.dateStart === 'string') ||
            !(typeof filters.client_id === 'string') ||
            !filters.filters.selectedTypes.length ||
            !filters.filters.selectedStatuses.length ||
            !filters.filters.selectedPageTypes.length
          ) {
            throw new Error('Invalid data')
          }
          const clientId = filters.client_id
          // we need to change client, return false
          if (clientId !== this.clientId) {
            if (!this.setNewClient(clientId, true)) {
              // user does not allowed to see this client, filters will be ignored
              ToasterService.showToast(ToasterService.ERROR_TYPE, 'client, with this client id, does not connected to current user')
              return
            }
          }
          Object.assign(this.filtersFromUrl, filters.filters, { calendarTypeView: filters.calendarTypeView })
          this.dateStart = filters.dateStart
        } catch (e) {
          ToasterService.showToast(ToasterService.ERROR_TYPE, 'Incorrect filter`s data')
        }
      }
    },
    /**
     * clear selected filters by setting them RESET action
     */
    clearFilters () {
      if (!this.enabledClearFiltersButton) {
        return
      }
      this.updateCopyFilterButton = true
      this.$set(this.filteredUrlStatuses, 'filterAction', FILTER_ACTION.RESET)
      this.$set(this.filteredTypes, 'filterAction', FILTER_ACTION.RESET)
      this.$set(this.filteredPageTypes, 'filterAction', FILTER_ACTION.RESET)
      this.$set(this.filters, 'selectedTypes', [])
      this.$set(this.filters, 'selectedStatuses', [])
      this.$set(this.filters, 'selectedPageTypes', [])
    },
    /**
     * method removes selected filters from URL when all filters from URL applied
     * if this.filtersFromUrl has entries dynamic update for filteredPageTypes disabled
     * it's fraught infinity rerender for {@link CalendarHeaderNavPanel}
     * also method disable RESET action when reset proccess in filters finished
     */
    clearResetAction () {
      this.$set(this.filteredUrlStatuses, 'filterAction', FILTER_ACTION.UPDATE)
      this.$set(this.filteredTypes, 'filterAction', FILTER_ACTION.UPDATE)
      this.$set(this.filteredPageTypes, 'filterAction', FILTER_ACTION.UPDATE)
      if (this.updateCopyFilterButton) {
        this.enabledCopyFiltersButton = false
        this.updateCopyFilterButton = false
      }
      if (Object.entries(this.filtersFromUrl).length) {
        const calendarApi = this.$refs.fullCalendar.getApi()
        calendarApi.changeView(this.filtersFromUrl.calendarTypeView)
        calendarApi.gotoDate(this.dateStart)
        // remove filters from calendar
        this.$router.replace('calendar')
        this.filtersFromUrl = {}
        this.enabledClearFiltersButton = true
      } else {
        this.enabledClearFiltersButton = false
      }
      this.updateFilteredEvents()
      this.updateFilteredPageTypes()
    },
    addEllipsis () {
      try {
        const events = this.$refs.fullCalendar.$el.querySelectorAll('.fc-content')
        events.forEach((event) => {
          if (event.scrollWidth < event.firstElementChild.offsetWidth) {
            event.classList.add('fc-ellipsis')
            const parentClassList = event.parentNode.classList
            switch (true) {
              case parentClassList.contains('calendar__url-planned'):
                event.classList.add('fc-ellipsis-url-planned')
                break
              case parentClassList.contains('calendar__url-published'):
                event.classList.add('fc-ellipsis-url-published')
                break
              case parentClassList.contains('calendar__url-proposed'):
                event.classList.add('fc-ellipsis-url-proposed')
                break
              case parentClassList.contains('calendar__event'):
                event.classList.add('fc-ellipsis-event')
                break
              case parentClassList.contains('calendar__link'):
                event.classList.add('fc-ellipsis-link')
                break
            }
          }
        })
      } catch (e) {
        console.log(e)
      }
    }
  }
}
</script>

<style lang="scss">
  @import '~@fullcalendar/core/main.css';
  @import '~@fullcalendar/daygrid/main.css';
  @import '~@fullcalendar/list/main.css';

  .serp-simulator {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    font-size: 12px;
    padding: 12px;
    width: 308px;
    max-width: 308px;

    .tooltip-inner {
      border: none !important;
      padding: 0 0 12px !important;
      border-bottom: 1px solid #dbdee5 !important;
      margin-bottom: 12px;
      border-radius: 0px !important;
      width: -webkit-fill-available !important;
      max-width: none !important;
      font-weight: 500;
    }

    &__title {
      color: #1a0dab !important;
      text-decoration: none;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      width: -webkit-fill-available;
      font-size: 15px;
      line-height: normal;
      margin: 0 0 4px;
      font-weight: 500;
    }

    &__url {
      text-decoration: none;
      display: flex;
      box-sizing: border-box;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      align-items: center;
      font-size: 12px;
      line-height: 1;
      color: #70757a;
      width: -webkit-fill-available;

      //
      //&::after {
      //  width: 0;
      //  height: 0;
      //  display: block;
      //  content: '';
      //  margin: 7px 3px 0;
      //  border-left: 5px solid transparent;
      //  border-right: 5px solid transparent;
      //  border-top: 4px solid $rfx-green;
      //}
    }

    &__description {
      line-height: 14px;
      font-weight: normal;
      text-decoration: none;
      text-overflow: ellipsis;
      overflow: hidden;
      box-sizing: border-box;
      display: -webkit-box;
      font-size: 12px;
      -webkit-line-clamp: 2;
      width: -webkit-fill-available;
      -webkit-box-orient: vertical;
      max-height: 28px;
      color: $rfx-greyish-brown;
    }
  }

  .event-week-view {
    display: flex;
    flex-direction: column;
    height: -webkit-fill-available;
    border-radius: 4px;

    &__desc,
    &__title {
      color: $rfx-medium-orange;
      font-size: 11px;
      line-height: 14px;
    }

    &__title {
      font-weight: 500;
    }

    &__desc {
      white-space: initial;
      height: 56px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
    }
  }

  $rfx-calendar-height: 800px;
  .calendar {
    flex: 1 1 100%;

    &__day-week-view {

      .calendar__url-planned,
      .calendar__url-published,
      .calendar__url-proposed {
        height: 86px;
        border: none !important;
        background-color: transparent !important;
        margin: 8px 0 0 !important;

        .fc-ellipsis {
          &:before {
            display: none;
          }
        }

        .fc-title {
          padding: 0 !important;
        }
      }

      .fc-title {
        width: -webkit-fill-available;
      }

      .calendar__url-planned,
      .calendar__url-published,
      .calendar__link,
      .calendar__event,
      .calendar__url-proposed {

        .fc-content {
          height: -webkit-fill-available;
        }

        .fc-title {
          height: -webkit-fill-available;
        }
      }

      .calendar__link {
        height: 86px;
        margin-top: 8px !important;

        .fc-title {
          white-space: break-spaces;
          padding: 8px;
          border-radius: 4px;
        }
      }

      .calendar__event {
        height: 86px;
        margin-top: 8px !important;

        .fc-title {
          padding: 8px;
        }
      }

      .serp-simulator {
        width: -webkit-fill-available;
        height: -webkit-fill-available;
        padding: 8px;
        background-color: transparent;
        border-radius: 4px;
        max-width: none;

        &__title,
        &__description,
        &__url {
          width: 100%;
        }

        &__description,
        &__url {
          font-size: 10px;
          line-height: 12px;
          margin-bottom: 4px;
        }

        &__title {
          font-size: 12px;
          line-height: normal;
          margin-bottom: 4px;
        }

        &__description {
          margin-bottom: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          max-height: 36px;
        }

        &__description {
          white-space: initial;
          max-height: none;
        }
      }

      .calendar__url-planned {
        .serp-simulator {
          background-color: #d6f3f1;
        }
      }

      .calendar__url-published {
        .serp-simulator {
          background-color: #24756d;

          &__title,
          &__description,
          &__url {
            color: #fff !important;

            span {
              color: #fff !important;
            }
          }
        }
      }

      .calendar__url-proposed {
        .serp-simulator {
          border: 1px solid #24756d;
        }
      }
    }

    &__tooltip {
      box-shadow: 0 6px 16px 0 rgba(96, 97, 112, 0.2), 0 1px 4px 0 rgba(40, 41, 61, 0.06);
      border-radius: 4px;
      background: #fff;
      margin-top: -4px;
    }

    &__wrapper {
      position: relative;

      .tooltip {
        z-index: 25;
        margin-top: -100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 0px;
        top: -4px !important;

        &-arrow-wrapper {
          height: 7px;
          width: 14px;
        }

        &-arrow {
          height: 10px;
          width: 10px;
          transform: rotate(45deg);
          margin-top: -5px;
          margin-left: 2px;
          border-bottom: 1px solid;
          border-right: 1px solid;
          border-top: none !important;
          border-left: none !important;
        }

        &-inner {
          padding: 9px;
          border-radius: 5px;
          border: 1px solid;
          width: max-content;
          max-width: 15vw;
          word-break: break-all;
        }

        &-inner-url-proposed {
          border: solid 1px #24756d;
          background-color: #fff;
          color: #24756d
        }

        &-inner-url-published {
          color: #fff;
          background-color: #24756d;
        }

        &-inner-url-planned {
          background-color: #D6F3F1;
          color: #24756d
        }

        &-inner-links {
          background-color: #EAEDFF;
          color: #284aff;
        }

        &-inner-event {
          background-color: #FFF6EA;
          color: #d88b23;
        }
      }
    }

    &__header-button-wrapper {
      border-radius: 15px;
      border: solid 1px #dbdee5;
      height: 28px;
      display: flex;
      padding: 0 10px;
      position: absolute;
      right: 70px;
      top: 54px;
      color: $rfx-dark-march;
    }

    &__show-weekends {
      margin-left: auto;

      .checkbox__label {
        color: $rfx-dark-march !important;
        font-size: 12px;
        font-weight: normal;
      }
    }

    &__copy-button {
      flex: 1 1 50%;
      height: 100%;
      border: none;
      outline: none;
      color: $rfx-dark-march;
      white-space: nowrap;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 90px;
      font-size: 11px;
      background-color: transparent;

      &:hover {
        opacity: 0.5;
      }
    }

    &__cross {
      font-size: 21px;
      margin-left: 5px;
      margin-top: -2px;
    }

    &__clear-filters {
      @extend .calendar__copy-button;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-left: 1px solid #dbdee5;
      width: auto;

      span:first-child {
        font-size: 11px;
      }

      span {
        color: $rfx-dark-march;
      }
    }

    &__today {
      padding: 0 17px;
      font-size: 10px;
      font-weight: 900;
      line-height: 40px;
      background-color: $rfx-medium-march;
      outline: none;
      border: none;
      color: #fff;
      text-align: center;
      margin-left: 28px;
      border-radius: 4px;
      letter-spacing: 1.25px;
    }

    &__title {
      font-size: 24px;
      line-height: 40px;
      color: $rfx-dark-march;
      font-weight: 300;
      margin: 0 0 0 24px;
    }

    &__title-wrapper {
      width: 100%;
      display: flex;
      align-items: center;
      margin: 16px 0 0;
      padding-right: 20px;
      box-sizing: border-box;
    }

    &__arrow-left {
      height: 0;
      border: solid black;
      border-width: 0 2px 2px 0;
      display: inline-block;
      padding: 5px;
      transform: rotate(135deg);
      cursor: pointer;
    }

    &__arrow-right {
      @extend .calendar__arrow-left;
      transform: rotate(-45deg);
      margin-left: 23px;
    }

    &__right-panel {
      position: absolute;
      top: -59px;
      right: 0;
      z-index: 25;
    }

    &__date-planner {
      z-index: 25;
      flex: 1 1 100%;
    }

    &__date-planner-wrapper {
      background-color: #fff;
      z-index: 25;
      margin-top: -57px;
      margin-left: 30px;
      height: calc(#{$rfx-calendar-height} + 246px);
      display: flex;
      /*width: 633px;*/
    }

    &__content-wrapper {
      display: flex;

      &_spread {
        margin-right: 30px;
      }
    }

    &__chips-block {
      height: 46px;
      position: absolute;
      top: 47px;
    }

    &__proposed {
      color: $rfx-dark-cyan !important;
      border: 1px solid $rfx-dark-cyan !important;
      background-color: transparent !important;
      cursor: pointer;
    }

    &__planned {
      color: $rfx-dark-cyan !important;
      border: 1px solid #d6f3f1 !important;
      background-color: #d6f3f1 !important;
      cursor: pointer;
    }

    &__published {
      color: #fff !important;
      border: 1px solid $rfx-dark-cyan;
      background-color: $rfx-dark-cyan !important;
      cursor: pointer;
    }

    &__event {
      color: $rfx-medium-orange !important;
      background-color: rgba(255, 166, 40, 0.1) !important;
      cursor: pointer;

      &.calendar-chips_selected:not(.calendar-chips_disabled),
      &.fc-event {
        border: none !important;
      }

      &:hover {
        color: $rfx-medium-orange;
      }
    }

    &__link {
      color: $rfx-bright-blue !important;
      background-color: rgba(40, 74, 255, 0.1) !important;

      &.calendar-chips_selected:not(.calendar-chips_disabled),
      &.fc-event {
        border: none !important;
      }

      &:hover {
        color: $rfx-bright-blue;
      }
    }

    &__url {
      color: #24756d !important;

      &.calendar-chips_selected {
        border: none !important;
      }
    }

    &__url-proposed {
      border: solid 1px #24756d !important;
      background-color: transparent !important;
      margin: 5px 0 !important;
      //
      //.fc-content {
      //  margin: 0 20px !important;
      //}
    }

    &__url-planned {
      background-color: #D6F3F1 !important;
      border: none !important;
    }

    &__url-published {
      color: #fff !important;
      background-color: #24756d !important;
      border: none !important;
    }

    &__cell {
      &_disabled {
        cursor: default !important;
      }
    }

    &__export-csv {
      position: absolute;
      top: 6px;
      right: -36px;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px);
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__show-weekend {
      //.fc-view-container {
      //  .serp-simulator {
      //    width: 92.3% !important;
      //  }
      //}
    }

    .headed-shifted {
      margin-bottom: 80px;
    }

    /******************************fullcalendar stuff*****************************/
    /*table*/
    .fc-day-header {
      border: none !important;
      padding: 16px 10px 10px !important;
    }

    .fc-day-number {
      padding: 10px !important;
      font-size: 18px !important;
      color: $rfx-dark-march !important;
      font-weight: normal !important;
    }

    td.fc-head-container {
      border: none !important;
    }

    .fc-toolbar > * > :not(:first-child) {
      margin-left: 0 !important;
    }

    .fc-day-header {
      font-size: 18px;
      color: $rfx-dark-march !important;
      opacity: 0.3 !important;
      font-weight: normal !important;
    }

    .fc-scroller.fc-day-grid-container {
      overflow-y: auto!important;
    }

    .fc-row.fc-widget-header {
      border-right-width: 0 !important;
    }

    /*buttons*/
    .fc-button-active,
    .fc-button {
      background-color: #ffffff !important;
      border: none transparent;
      border-radius: 0;
      outline: none !important;
      color: $rfx-dark-march;
      font-size: 10px;
      font-weight: 900;
      padding: 8px 20px;
      text-transform: uppercase;
      border-right: 1px solid $rfx-light-steel;
      height: 40px;

      &:disabled {
        opacity: 1;
        color: $rfx-steel;
      }

      &:hover,
      &:focus {
        outline: none !important;
        color: $rfx-dark-march;
        box-shadow: none;
      }

      &:hover {
        background-color: $rfx-light-steel !important;
      }

      &:last-child {
        border-right: 0;
      }
    }

    .fc-button-active {
      background-color: $rfx-light-steel !important;
      color: $rfx-dark-march !important;

    }

    .fc-more-popover {
      width: 281px;
      box-shadow: 0 6px 16px 0 rgba(96, 97, 112, 0.2), 0 1px 4px 0 rgba(40, 41, 61, 0.06);

      .fc-widget-header {
        background-color: #fff !important;
        padding: 16px 16px 12px;

        .fc-title {
          padding: 0;
          font-size: 14px;
          font-weight: 500;
          line-height: 16px;
          color: $rfx-dark-march;
        }
      }

      .fc-widget-content {
        .fc-event-container {
          padding: 0 16px 16px !important;
        }
      }

      .fc-icon-x {
        position: relative;
        overflow: visible;
        opacity: 1;
        &:before {
          content: none;
        }
        &:after {
          top: -12px;
          right: 0;
          position: absolute;
          font-size: 25px;
          color: $rfx-dark-march;
        }
      }
    }

    .fc-right,
    .fc-left {
      background-color: transparent !important;
      border: solid 1px $rfx-light-steel !important;
      border-radius: 4px !important;
    }

    .fc-right {
      position: relative;
      bottom: 43px;
    }

    .fc-button-primary:not(:disabled).fc-button-active,
    .fc-button-primary:not(:disabled):active {
      border-color: transparent;
    }

    .fc-button-primary:not(:disabled).fc-button-active:focus,
    .fc-button-primary:not(:disabled):active:focus {
      box-shadow: none;
    }

    /*events*/
    .fc-content {
      overflow: visible;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    .fc-ellipsis {
      &:before {
        content: '...';
        position: absolute;
        width: 21px;
        left: -1px;
        font-weight: bold;
        bottom: 4px;
        font-size: 16px;
        text-align: end;
      }
    }

    .fc-ellipsis-url-proposed {
      &:before {
        background-color: #fff;
      }
    }

    .fc-ellipsis-url-planned {
      &:before {
        background-color: #D6F3F1;
      }
    }

    .fc-ellipsis-url-published {
      &:before {
        background-color: #24756d;
      }
    }

    .fc-ellipsis-link {
      &:before {
        background-color: #EAEDFF;
      }
    }

    .fc-ellipsis-event {
      &:before {
        background-color: #FFF6EA;
      }
    }

    .fc-title {
      margin-right: auto;
      padding: 6px 8px;
    }

    .fc-toolbar {
      margin-bottom: 6px;
    }

    .fc-time {
      display: none;
    }

    .fc-center h2 {
      @extend .calendar__title
    }

    .fc-event {
      overflow: hidden;
    }

    .fc-event-container {
      padding: 0 10px !important;
      overflow-y: auto;
    }

    .fc-scroller {
      overflow-y: hidden !important;
    }

    a.fc-more {
      text-align: center;
      display: block;
      font-size: 14px;
      line-height: 16px;
      color: $rfx-shaded-grey;
      padding-right: 5px;
      text-decoration: underline;
      margin-top: 7px;
      &:after {
        content: ' more items';
      }
    }

    /*title*/
    .fc-center {
      position: absolute;
      top: 0;
      left: 194px;

    }

    .fc-dayGridMonth-view {
      .fc-event {
        margin-bottom: 8px !important;
      }
    }

    .fc-day-grid-event {
      margin: 0;
      padding: 0;
    }
  }
</style>
